import React, { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Scale, X, ShoppingCart, Truck, MapPin, Package, Store, Star, Trash2 } from "lucide-react";
import { useCompare } from "@/context/CompareContext";
import { useCart } from "@/context/CartContext";
import { jmd, CATEGORY_LABELS } from "@/lib/format";
import { assetUrl } from "@/lib/api";

export default function Compare() {
  const { items, removeCompare, clearCompare } = useCompare();
  const { addToCart } = useCart();
  const navigate = useNavigate();

  const specKeys = useMemo(() => {
    const keys = [];
    items.forEach((p) => Object.keys(p.specs || {}).forEach((k) => { if (!keys.includes(k)) keys.push(k); }));
    return keys;
  }, [items]);

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="compare-empty">
        <div className="h-16 w-16 mx-auto grid place-items-center rounded-2xl bg-secondary mb-5">
          <Scale size={30} className="text-ink" />
        </div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Nothing to compare yet</h1>
        <p className="text-muted-foreground mt-2">Add products using the compare button on any product to weigh them side by side.</p>
        <Link to="/search" className="inline-flex items-center gap-2 mt-6 bg-ink text-white rounded-xl px-6 py-3 font-semibold hover:bg-coral transition-colors">
          Browse products
        </Link>
      </div>
    );
  }

  const rowClass = "border-b border-border";
  const labelClass = "text-sm font-semibold text-muted-foreground align-top py-4 pr-4 w-40 sticky left-0 bg-background";

  const priceCells = items.map((p) => (p.price_on_request ? "req" : p.price));
  const priceDiffer = new Set(priceCells).size > 1;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Scale size={22} className="text-coral" />
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Compare Products</h1>
        </div>
        <button data-testid="compare-clear-all" onClick={clearCompare}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-coral transition-colors">
          <Trash2 size={15} /> Clear all
        </button>
      </div>

      <div className="overflow-x-auto sn-scroll-x rounded-2xl border border-border">
        <table className="w-full border-collapse" data-testid="compare-table">
          <tbody>
            {/* Product header */}
            <tr className={rowClass}>
              <td className={labelClass}></td>
              {items.map((p) => (
                <td key={p.id} className="p-4 align-top min-w-[220px]" data-testid={`compare-col-${p.id}`}>
                  <div className="relative">
                    <button data-testid={`compare-remove-${p.id}`} onClick={() => removeCompare(p.id)}
                      className="absolute top-1 right-1 h-7 w-7 grid place-items-center rounded-full bg-white/90 border border-border hover:bg-coral hover:text-white transition-colors z-10">
                      <X size={14} />
                    </button>
                    <Link to={`/product/${p.id}`} className="block aspect-square rounded-xl overflow-hidden bg-slate-100">
                      <img src={assetUrl(p.image)} alt={p.name} className="h-full w-full object-cover" />
                    </Link>
                  </div>
                  <Link to={`/product/${p.id}`} className="block font-display font-semibold text-[15px] leading-snug line-clamp-2 mt-3 hover:text-emerald transition-colors">
                    {p.name}
                  </Link>
                </td>
              ))}
            </tr>

            {/* Price */}
            <tr className={rowClass}>
              <td className={labelClass}>Price</td>
              {items.map((p) => (
                <td key={p.id} className={`p-4 align-top ${priceDiffer ? "bg-coral/5" : ""}`}>
                  {p.price_on_request ? (
                    <span className="font-display font-bold text-coral">Request Current Price</span>
                  ) : (
                    <div>
                      <span className="font-display font-extrabold text-xl text-ink">{jmd(p.price)}</span>
                      {p.prev_price > p.price && <span className="block text-sm text-muted-foreground line-through">{jmd(p.prev_price)}</span>}
                    </div>
                  )}
                </td>
              ))}
            </tr>

            {/* Brand */}
            <tr className={rowClass}>
              <td className={labelClass}>Brand</td>
              {items.map((p) => (
                <td key={p.id} className="p-4 align-top text-sm"><span className="flex items-center gap-1.5"><Store size={14} className="text-emerald" /> {p.brand || "—"}</span></td>
              ))}
            </tr>

            {/* Rating */}
            <tr className={rowClass}>
              <td className={labelClass}>Rating</td>
              {items.map((p) => (
                <td key={p.id} className="p-4 align-top text-sm">
                  <span className="flex items-center gap-1.5"><Star size={14} className="fill-gold text-gold" /> {p.rating || 0} · {p.reviews || 0} reviews</span>
                </td>
              ))}
            </tr>

            {/* Category */}
            <tr className={rowClass}>
              <td className={labelClass}>Category</td>
              {items.map((p) => (
                <td key={p.id} className="p-4 align-top text-sm capitalize">{CATEGORY_LABELS[p.category] || (p.category || "—").replace("-", " ")}</td>
              ))}
            </tr>

            {/* Parish */}
            <tr className={rowClass}>
              <td className={labelClass}>Location</td>
              {items.map((p) => (
                <td key={p.id} className="p-4 align-top text-sm"><span className="flex items-center gap-1.5"><MapPin size={14} className="text-emerald" /> {p.parish || "—"}</span></td>
              ))}
            </tr>

            {/* Delivery */}
            <tr className={rowClass}>
              <td className={labelClass}>Delivery</td>
              {items.map((p) => (
                <td key={p.id} className="p-4 align-top text-sm"><span className="flex items-center gap-1.5"><Truck size={14} className="text-emerald" /> {p.delivery_days}-day</span></td>
              ))}
            </tr>

            {/* Stock */}
            <tr className={rowClass}>
              <td className={labelClass}>Availability</td>
              {items.map((p) => (
                <td key={p.id} className="p-4 align-top text-sm">
                  <span className="flex items-center gap-1.5"><Package size={14} className="text-emerald" /> {p.stock > 0 ? `${p.stock} in stock` : "Out of stock"}</span>
                </td>
              ))}
            </tr>

            {/* Dynamic specs */}
            {specKeys.map((key) => (
              <tr key={key} className={rowClass}>
                <td className={labelClass}>{key}</td>
                {items.map((p) => (
                  <td key={p.id} className="p-4 align-top text-sm">{(p.specs || {})[key] ?? "—"}</td>
                ))}
              </tr>
            ))}

            {/* Actions */}
            <tr>
              <td className={labelClass}></td>
              {items.map((p) => (
                <td key={p.id} className="p-4 align-top">
                  {p.price_on_request ? (
                    <Link to={`/product/${p.id}`} data-testid={`compare-view-${p.id}`}
                      className="flex items-center justify-center gap-1.5 rounded-xl bg-ink text-white py-2.5 text-sm font-semibold hover:bg-coral transition-colors">
                      View · Request Price
                    </Link>
                  ) : (
                    <button data-testid={`compare-add-${p.id}`} onClick={() => addToCart(p)}
                      className="w-full flex items-center justify-center gap-1.5 rounded-xl bg-ink text-white py-2.5 text-sm font-semibold hover:bg-emerald-deep transition-colors">
                      <ShoppingCart size={15} /> Add to Cart
                    </button>
                  )}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
